import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../utils/api'

interface LatestOrder {
  id: string
  status: string
  report_id: string | null
  progress?: {
    current_stage: string | null
    progress_percent: number
    progress_message: string | null
  }
}

export default function StatusIndicator() {
  const [apiStatus, setApiStatus] = useState<'checking' | 'online' | 'offline'>('checking')
  const [latestOrder, setLatestOrder] = useState<LatestOrder | null>(null)

  useEffect(() => {
    const checkStatus = async () => {
      try {
        // Check API health
        await api.get('/health')
        setApiStatus('online')

        // Get latest order with progress
        const ordersRes = await api.get('/orders')
        const orders = ordersRes.data

        if (orders.length > 0) {
          const latest = orders[0]

          // If processing, get progress details
          if (latest.status === 'processing' || latest.status === 'paid') {
            if (latest.report_id) {
              try {
                const progressRes = await api.get(`/report/${latest.report_id}/progress`)
                setLatestOrder({
                  ...latest,
                  progress: {
                    current_stage: progressRes.data.current_stage,
                    progress_percent: progressRes.data.progress_percent,
                    progress_message: progressRes.data.progress_message
                  }
                })
              } catch {
                setLatestOrder(latest)
              }
            } else {
              setLatestOrder(latest)
            }
          } else {
            setLatestOrder(latest)
          }
        }
      } catch {
        setApiStatus('offline')
      }
    }

    checkStatus()
    const interval = setInterval(checkStatus, 5000) // Poll every 5s for progress updates
    return () => clearInterval(interval)
  }, [])

  // Handle both uppercase and lowercase status values
  const orderStatus = latestOrder?.status?.toLowerCase()
  const isProcessing = latestOrder && (orderStatus === 'processing' || orderStatus === 'paid')
  const isComplete = latestOrder && (orderStatus === 'complete')

  return (
    <div className="flex flex-col items-end gap-1">
      {/* API Status */}
      <div className="flex items-center gap-2 text-xs text-dark-400">
        <div className="relative flex items-center justify-center">
          {apiStatus === 'online' && (
            <>
              <span className="absolute inline-flex h-2 w-2 rounded-full bg-green-400 opacity-75 animate-ping"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
            </>
          )}
          {apiStatus === 'offline' && (
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
          )}
          {apiStatus === 'checking' && (
            <span className="relative inline-flex h-2 w-2 rounded-full bg-yellow-500 animate-pulse"></span>
          )}
        </div>
        <span>
          {apiStatus === 'online' && 'Systems Online'}
          {apiStatus === 'offline' && 'Systems Offline'}
          {apiStatus === 'checking' && 'Checking...'}
        </span>
      </div>

      {/* Latest Report Progress */}
      {isProcessing && latestOrder.progress && (
        <Link
          to={latestOrder.report_id ? `/report/${latestOrder.report_id}` : '#'}
          className="mt-2 p-3 bg-dark-800/80 border border-ginger-500/30 rounded-lg hover:border-ginger-500/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-8 h-8 rounded-full border-2 border-dark-600">
                <svg className="w-8 h-8 -rotate-90">
                  <circle
                    cx="16"
                    cy="16"
                    r="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-dark-700"
                  />
                  <circle
                    cx="16"
                    cy="16"
                    r="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray={`${(latestOrder.progress.progress_percent / 100) * 88} 88`}
                    className="text-ginger-500"
                  />
                </svg>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[10px] font-bold text-ginger-400">{latestOrder.progress.progress_percent}%</span>
              </div>
            </div>
            <div className="text-left">
              <p className="text-xs font-medium text-ginger-400">
                {latestOrder.progress.current_stage || 'Processing...'}
              </p>
              <p className="text-[10px] text-dark-400 max-w-[150px] truncate">
                {latestOrder.progress.progress_message || 'Scanning in progress'}
              </p>
            </div>
          </div>
        </Link>
      )}

      {/* Completed Report */}
      {isComplete && latestOrder.report_id && (
        <Link
          to={`/report/${latestOrder.report_id}`}
          className="mt-2 flex items-center gap-2 text-xs text-green-400 hover:text-green-300"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Latest report ready</span>
        </Link>
      )}
    </div>
  )
}
